import type {
  CreateTax,
  Tax,
  TaxQuery,
  UpdateTax,
} from '@monorepo/shared/schemas/taxes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/shared/lib/toast'
import { taxService } from '../services/taxService'

// Helper to extract error messages
function extractErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const err = error as {
      response?: { data?: { error?: { message?: string }; message?: string } }
      message?: string
    }
    return (
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message ||
      'An error occurred'
    )
  }
  return 'An error occurred'
}

// ============================================================================
// QUERY KEYS (for cache management)
// ============================================================================

export const taxKeys = {
  all: ['taxes'] as const,
  lists: () => [...taxKeys.all, 'list'] as const,
  list: (filters?: TaxQuery) => [...taxKeys.lists(), filters] as const,
  details: () => [...taxKeys.all, 'detail'] as const,
  detail: (id: string) => [...taxKeys.details(), id] as const,
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Get paginated list of taxes with filters
 */
export function useTaxes(filters?: TaxQuery) {
  return useQuery({
    queryKey: taxKeys.list(filters),
    queryFn: () => taxService.getTaxes(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  })
}

/**
 * Convenience hook that returns just the taxes array
 */
export function useTaxesList(filters?: TaxQuery) {
  const query = useTaxes(filters)
  const taxes = (query.data?.data || []) as Tax[]

  return {
    ...query,
    taxes,
    pagination: query.data?.pagination,
  }
}

/**
 * Get single tax by ID
 */
export function useTax(id: string) {
  return useQuery({
    queryKey: taxKeys.detail(id),
    queryFn: () => taxService.getTax(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new tax
 */
export function useCreateTax() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTax) => {
      return taxService.createTax(data)
    },
    onSuccess: newTax => {
      // Invalidate all list queries (including inactive ones)
      queryClient.invalidateQueries({
        queryKey: taxKeys.lists(),
        refetchType: 'all',
      })

      // Add to detail cache
      queryClient.setQueryData(taxKeys.detail(newTax.id), newTax)

      toast.success(`"${newTax.name}" has been created successfully.`)
    },
    onError: error => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Update an existing tax
 */
export function useUpdateTax() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTax }) => {
      return await taxService.updateTax(id, data)
    },
    onSuccess: updatedTax => {
      // Update detail cache for immediate UI update
      queryClient.setQueryData(taxKeys.detail(updatedTax.id), updatedTax)

      // Invalidate list queries to refetch with updated data (including inactive queries)
      queryClient.invalidateQueries({
        queryKey: taxKeys.lists(),
        refetchType: 'all',
      })

      toast.success(`"${updatedTax.name}" has been updated successfully.`)
    },
    onError: error => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Delete a tax
 */
export function useDeleteTax() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => taxService.deleteTax(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: taxKeys.detail(id) })

      // Invalidate lists (including inactive queries)
      queryClient.invalidateQueries({
        queryKey: taxKeys.lists(),
        refetchType: 'all',
      })

      toast.success('The tax has been deleted successfully.')
    },
    onError: error => {
      toast.error(extractErrorMessage(error))
    },
  })
}
